import { ProjectFilters } from '@/data/services/projectServices';
import { projectsQuerykeys } from '@/data/services/querykeys';
import Hackathon from '@/models/Hackathon';
import { UseQueryOptions } from '@tanstack/react-query';
import { uniqBy } from 'lodash';
import useInfiniteService from '../useInfiniteService';
import useWinners from './useWinners';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';

export function useRanking(
  hackathon: Hackathon,
  filters: ProjectFilters = {},
  options: Pick<UseQueryOptions, 'enabled'> = {},
) {
  const { winnersByRank, winnersByCategory, winners } = useWinners(
    hackathon,
    options,
  );
  const podiumByCourse = winnersByCategory[filters.courseType?.at(0) || '']
    ?.map(({ project, rank }) => ({ ...project, rank }))
    .sort((a, b) => a.rank - b.rank);

  const podiumRanking = Object.values(winnersByRank)
    .flat()
    .map(({ rank, project }) => ({
      ...project,
      rank,
    }));
  const podium = podiumByCourse || podiumRanking;

  const allFilters: ProjectFilters = {
    byUpVotes: 'more',
    flagsId: [hackathon.flagId],
    ...filters,
  };

  const { queryFn, queryKey } =
    projectsQuerykeys.list(allFilters)._ctx.infinity;

  const { results, ...rest } = useInfiniteService({
    queryFn,
    queryKey,
    ...options,
    staleTime: REQUEST_STALE_TIME_IN_MS,
  });

  const projectsFiltered = results.filter(
    project => !winners.map(e => e.project.id).includes(project.id),
  );
  const restProjects = projectsFiltered.map((project, index) => ({
    ...project,
    rank: index + 4,
  }));

  const projectRanking = podium.concat(restProjects);
  return {
    results: uniqBy(projectRanking, 'id'),
    podium,
    winners,
    restProjects,
    queryKey,
    ...rest,
  };
}
